import { memo, useCallback, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import styled from 'styled-components';
import Loader from 'components/common/loader/Loader';
import { CardWrap } from '../../CardWrap';
import { MetricCardHeader } from './MetricCardHeader/MetricCardHeader';
import { MetricCardRow } from './MetricCardRow/MetricCardRow';
import { dashboardAPI } from 'services';

const CARD_HEIGHT = '470px';

const METRICS_ADVERTISER = ['installs', 'cost'];
const METRICS_PUBLISHER = ['revenue', 'eCPM'];

// TYPES:
// 'Applications'
// 'ApplicationsPublisher'
// 'Campaigns'
// 'Events/Installs Ratio'
// 'Top Events'

function getRows(type, data) {
  if (type === 'Campaigns' || type === 'Events/Installs Ratio') return data.campaigns_data || [];
  return data;
}

function getSelectedMetric(type) {
  if (type === 'Top Events') return 'value';
  if (type === 'ApplicationsPublisher') return METRICS_PUBLISHER[0];
  return METRICS_ADVERTISER[0];
}

function getRequestType(type) {
  if (type === 'Applications') return 'useGetApplicationsBlockQuery';
  if (type === 'ApplicationsPublisher') return 'useGetPublisherApplicationsBlockQuery';
  if (type === 'Campaigns' || type === 'Events/Installs Ratio') return 'useGetCampaignsBlockQuery';
  if (type === 'Top Events') return 'useGetTopEventsBlockQuery';

  return null;
}

function getMetrics(type) {
  if (type === 'ApplicationsPublisher') return METRICS_PUBLISHER;
  return METRICS_ADVERTISER;
}

export const MetricCard = memo(({ type, numCols, timePeriod }) => {
  const [selectedMetric, setSelectedMetric] = useState(() => getSelectedMetric(type));
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedEventInd, setSelectedEventInd] = useState(0);
  const [sortDirection, setSortDirection] = useState('desc');

  const requestType = getRequestType(type);
  const requestHook = dashboardAPI[requestType] || dashboardAPI.useGetApplicationsBlockQuery;
  const metrics = getMetrics(type);

  const { data: requestData, isFetching } = requestHook(timePeriod, {
    skip: !requestType || !timePeriod?.from || !timePeriod?.to,
  });

  const data = useMemo(() => requestData || [], [requestData]);
  const rows = useMemo(() => getRows(type, data), [type, data]);

  const countries = useMemo(() => {
    if (type !== 'ApplicationsPublisher') return null;

    const countryMap = new Map();

    rows.forEach((item) => {
      item.country_codes?.forEach((code, ind) => {
        if (!code || countryMap.has(code)) return;

        countryMap.set(code, {
          code,
          name: item.countries?.[ind] || code,
        });
      });
    });

    return Array.from(countryMap.values());
  }, [rows, type]);

  const filteredRows = useMemo(() => {
    if (type !== 'ApplicationsPublisher' || !selectedCountry) return rows;

    return rows.filter((item) => item.country_codes?.includes(selectedCountry));
  }, [rows, selectedCountry, type]);

  const totalRevenue = useMemo(() => {
    if (type !== 'ApplicationsPublisher') return 0;

    return filteredRows.reduce((sum, item) => sum + (Number(item.revenue) || 0), 0);
  }, [filteredRows, type]);

  const events = useMemo(() => {
    if (type === 'Events/Installs Ratio') return data.event_titles || [];
    return [];
  }, [data, type]);

  const metricRows = useMemo(() => {
    return type === 'Events/Installs Ratio'
      ? filteredRows.map((item) => {
          const event = item.event_values?.[selectedEventInd];
          const eventPercentValue = item[selectedMetric]
            ? (event / item[selectedMetric]) * 100
            : event
            ? 100
            : 0;

          return {
            ...item,
            event,
            eventPercentValue,
          };
        })
      : filteredRows;
  }, [filteredRows, selectedEventInd, selectedMetric, type]);

  const sortedRows = useMemo(() => {
    return [...metricRows].sort((a, b) => {
      const firstValue =
        type === 'Events/Installs Ratio'
          ? Number(a.eventPercentValue) || 0
          : Number(a[selectedMetric]) || 0;
      const secondValue =
        type === 'Events/Installs Ratio'
          ? Number(b.eventPercentValue) || 0
          : Number(b[selectedMetric]) || 0;

      // sort logic when values are equal
      if (firstValue === secondValue) {
        const firstName = (a.name ?? '').trim();
        const secondName = (b.name ?? '').trim();

        const firstGroup = /^\p{L}/u.test(firstName) ? 0 : /^\p{N}/u.test(firstName) ? 1 : 2;
        const secondGroup = /^\p{L}/u.test(secondName) ? 0 : /^\p{N}/u.test(secondName) ? 1 : 2;
        if (firstGroup !== secondGroup) {
          return sortDirection === 'asc' ? firstGroup - secondGroup : secondGroup - firstGroup;
        }

        const nameDifference = firstName.localeCompare(secondName, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        return sortDirection === 'asc' ? nameDifference : -nameDifference;
      }

      return sortDirection === 'asc' ? firstValue - secondValue : secondValue - firstValue;
    });
  }, [metricRows, selectedMetric, sortDirection, type]);

  const renderRow = useCallback(
    (index, item) => (
      <MetricCardRow
        type={type}
        data={item}
        metric={selectedMetric}
        eventPercentValue={item.eventPercentValue}
        showProgressBar={type === 'ApplicationsPublisher' && selectedMetric === 'revenue'}
        progressValue={totalRevenue ? (Number(item.revenue) || 0) / totalRevenue : 0}
      />
    ),
    [type, selectedMetric, totalRevenue],
  );

  if (type === 'Top Events' && !data?.length) {
    return null;
  }

  if (type === 'Events/Installs Ratio' && !events?.length) {
    return null;
  }

  return (
    <CardWrap
      numCols={numCols}
      style={{
        height: CARD_HEIGHT,
        paddingTop: '24px',
        overflowY: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <MetricCardHeader
        type={type}
        metrics={metrics}
        events={events}
        isTextIcons={type === 'ApplicationsPublisher'}
        countries={countries}
        selectedCountry={selectedCountry}
        selectedMetric={selectedMetric}
        selectedEventInd={selectedEventInd}
        sortDirection={sortDirection}
        onMetricChange={setSelectedMetric}
        onCountryChange={setSelectedCountry}
        onEventChange={setSelectedEventInd}
        onSortChange={setSortDirection}
        isFetching={isFetching}
      />
      {isFetching ? (
        <LoaderWrap>
          <Loader.Local delay={0} />
        </LoaderWrap>
      ) : (
        <RowsWrap
          data={sortedRows}
          computeItemKey={(index, item) => item.id ?? index}
          itemContent={renderRow}
        />
      )}
    </CardWrap>
  );
});

MetricCard.displayName = 'MetricCard';

const LoaderWrap = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RowsWrap = styled(Virtuoso)`
  hight: unset;
  flex-grow: 1;
  overflow-y: scroll !important;
  margin-right: -6px;

  > * {
    box-sizing: border-box;
    padding-right: 6px;
  }

  ::-webkit-scrollbar {
    width: 14px;
    background-color: transparent;
  }

  ::-webkit-scrollbar-track {
    margin: 10px;
    width: 6px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border: 4px solid #fff;
    border-radius: 8px;
  }
`;
